import 'animate.css';
import './index.less';

import { ANONYMOUS_USER_ID, STATIC_FILE_URL } from '@/shared/constants';
import { EventName, EventSource } from '@/shared/constants/event';
import { goToContactSales, setAnonymousUser } from '@/shared/utils/utils';
import { useConfigProvider, useCreateAccountProvider } from '@/lib/context';

import CopilotEntrypoint from './CopilotEntrypoint';
import { HOMEPAGE_V4_INTRO_LOGOS } from '@/shared/constants/homepage-constants';
import HomepageV4IntroCards from './Cards';
import Image from 'next/image';
import React from 'react';
import ResponsiveContent from '@/components/Common/ResponsiveContent';
import queryString from 'query-string';
import { sendEventAnalyses } from '@/lib/service';
import storage from '@/shared/utils/storage';
import { useRouter } from 'next/navigation';

const HomePageIntro = () => {
  const { isMobile } = useConfigProvider();

  const router = useRouter();
  const { showSignupDialogFromSourcing } = useCreateAccountProvider();

  const handleJoinPietra = () => {
    sendEventAnalyses({
      eventName: EventName.COMMON_VIEW,
      commonType: EventSource.HOMEPAGE,
      commonContent: 'join_pietra',
      sourceClient: isMobile ? 'mobile_web' : 'desktop_web',
      anonymousUserId: storage.get(ANONYMOUS_USER_ID) ?? setAnonymousUser(),
    });

    router.push('/sell-on-pietra/register');
  };

  const openCopilotQuery = (query: string, eventSource: string) => {
    showSignupDialogFromSourcing(
      'To use Pietra AI, please create an account first.',
      queryString.stringifyUrl({
        url: '/homebase/quiz',
        query: {
          query,
        },
      }),
      eventSource,
    );
  };

  return (
    <div className="home-page-intro-wrap">
      <div className="padded-container">
        <div className="home-page-intro-header-wrap">
          <div className="title">Finally! An AI platform that helps you run your business.</div>
          <div className="subtitle">
            Pietra gives your business a private AI that automates operations and marketing tasks.
          </div>
        </div>

        <CopilotEntrypoint
          onClickSend={(input: string) => {
            openCopilotQuery(input, 'pietra-ai-entrypoint');
          }}
          onClickCreateAccount={handleJoinPietra}
          onClickBookDemo={goToContactSales}
        />

        <div className="home-page-intro-copilot-explanation-wrapper">
          <div className="card">
            <span className="title">The only AI that connects to your real time data</span>
            <div className="integrations">
              <div className="logo">
                <Image width={15} height={16} src="/svg/shopify.svg" alt="Shopify" />
              </div>
              <div className="logo">
                <Image width={14} height={16} src="/svg/meta.svg" alt="Meta Ads" />
              </div>
              <div className="logo">
                <Image width={17} height={16} src="/svg/google.svg" alt="Google" />
              </div>
              <div className="logo">
                <Image width={17} height={18} src="/svg/instagram.svg" alt="Instagram" />
              </div>
              <div className="logo">
                <Image width={17} height={18} src="/svg/tiktok-color.svg" alt="TikTok" />
              </div>
              <div className="logo">
                <Image
                  width={16}
                  height={16}
                  src={`${STATIC_FILE_URL}/372423df5299ffc5ab09fc537638baa6.png`}
                  alt="Word"
                />
              </div>
              <div className="logo">
                <Image
                  width={13}
                  height={16}
                  src={`${STATIC_FILE_URL}/f4cca6162680479ad7e51f7fd967ea42.png`}
                  alt="Adobe Acrobat"
                />
              </div>
              <div className="logo">
                <Image
                  width={12}
                  height={16}
                  src={`${STATIC_FILE_URL}/bffd2a2694d83842798572a60c58373a.png`}
                  alt="Document"
                />
              </div>
              <div className="logo">
                <Image
                  width={12}
                  height={16}
                  src={`${STATIC_FILE_URL}/a962cc5a0e19069621a9f90b18d3ad5e.png`}
                  alt="Excel Spreadsheet"
                />
              </div>
              <div className="logo">
                <Image
                  width={18}
                  height={16}
                  src={`${STATIC_FILE_URL}/ba11b32e03756183d3990d9e79399de8.png`}
                  alt="Google Drive"
                />
              </div>
            </div>
          </div>
          <div className="card">
            <span className="title">Picks the best model for the task</span>
            <div className="ecommerce">
              <img src={`${STATIC_FILE_URL}/c915eadeac1f7d8a34dec2e5b6e4138b.svg`} alt="Google Gemini" />
              <span className="text">Creating 3 ad concepts using Google Gemini</span>
            </div>
            <div className="ecommerce">
              <img src={`${STATIC_FILE_URL}/8a33e492c76ecac287b80b8f75ce50f8.svg`} alt="Claude" />
              <span className="text">Creating sales & analytics report using Claude</span>
            </div>
          </div>
        </div>

        <div className="home-page-intro-rating-container">
          <div className="title">Trusted by leading commerce teams</div>
          <div className="image-list-container">
            <div className="image-list">
              {HOMEPAGE_V4_INTRO_LOGOS.map((e, index) => (
                <Image
                  key={index}
                  width={isMobile ? e.width / 1.35 : e.width}
                  height={isMobile ? e.height / 1.35 : e.height}
                  src={e.src}
                  alt={e.alt}
                  className="logo"
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="home-page-intro-header-text">
        <ResponsiveContent
          set={[
            ['Automate your operations & marketing with AI agents', 720],
            ['Reclaim 20+ hours each week by using AI to help run your operations'],
          ]}
        />
      </div>

      <HomepageV4IntroCards />
    </div>
  );
};

export default HomePageIntro;
